import * as React from 'react';
import { Alert } from 'antd';
import { ExportOutlined, SearchOutlined, StarFilled } from '@ant-design/icons';
import { TableColumn } from 'react-data-table-component';
import moment from 'jalali-moment';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Table } from 'components/table/table';
import { Badge, UserLink, CopyablePhone } from 'components';
import { Input } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import { PaginationList, UserScoreLogWithUser } from 'core/domain';
import { getAllUserScoreLogs } from 'core/repositories/user-score';
import { downloadCSV } from 'core/utils/export-to-file';
import { faNumToEnNum } from 'core/utils/decimalExtentions';
import { scoreTypeBadge } from './score-type';
import './user-score-logs.scss';

const PAGE_SIZE = 20;

const buildQuery = (kw: string): string => (kw ? `?Keyword=${kw}` : '');

const fullNameOf = (row: UserScoreLogWithUser) =>
    `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();

const flattenForExport = (items: UserScoreLogWithUser[]) =>
    items.map((row) => ({
        'نام و نام‌خانوادگی': fullNameOf(row) || '',
        'شماره موبایل': row.phoneNumber || '',
        امتیاز: row.score,
        'نوع امتیاز': row.scoreType?.title || '',
        زیرمجموعه: row.referralUserId ? 'دارد' : '',
        تاریخ: row.createdOn
            ? moment.utc(row.createdOn).locale('fa').local().format('YYYY/MM/DD HH:mm')
            : '',
    }));

export const UserScoreLogs: React.FC = () => {
    const [data, setData] = React.useState<PaginationList<UserScoreLogWithUser>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [pageSize, setPageSize] = React.useState<number>(PAGE_SIZE);
    const [accessDenied, setAccessDenied] = React.useState<boolean>(false);
    const [keyword, setKeyword] = React.useState<string>('');
    const [query, setQuery] = React.useState<string>('');

    React.useEffect(() => {
        getAllUserScoreLogs(setLoading, setData, 1, PAGE_SIZE, '', setAccessDenied);
    }, []);

    // Debounced keyword search
    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            const q = buildQuery(faNumToEnNum(keyword).trim());
            setQuery(q);
            setAccessDenied(false);
            getAllUserScoreLogs(setLoading, setData, 1, PAGE_SIZE, q, setAccessDenied);
        }, 500);
        return () => clearTimeout(timer);
    }, [keyword]);

    const columns: TableColumn<UserScoreLogWithUser>[] = [
        {
            name: 'نام و نام‌خانوادگی',
            id: 'fullName',
            selector: (row) => fullNameOf(row) || '-',
            width: '180px',
            cell: (row) => (
                <UserLink userId={row.userId}>
                    <span>{fullNameOf(row) || <span className="empty-val">—</span>}</span>
                </UserLink>
            ),
        },
        {
            name: 'شماره موبایل',
            id: 'phoneNumber',
            selector: (row) => row.phoneNumber,
            width: '150px',
            cell: (row) => (
                <CopyablePhone phoneNumber={row.phoneNumber} />
            ),
        },
        {
            name: 'امتیاز',
            id: 'score',
            selector: (row) => row.score,
            width: '110px',
            cell: (row) => (
                <span className="score-cell">
                    <StarFilled className="score-star" />
                    {row.score ?? '—'}
                </span>
            ),
        },
        {
            name: 'نوع امتیاز',
            id: 'scoreType',
            selector: (row) => row.scoreType?.title || '',
            width: '170px',
            cell: (row) =>
                row.scoreType ? (
                    <Badge text={row.scoreType.title} type={scoreTypeBadge(row.scoreType.id)} />
                ) : (
                    <span className="empty-val">—</span>
                ),
        },
        {
            name: 'زیرمجموعه',
            id: 'referral',
            width: '110px',
            cell: (row) =>
                row.referralUserId ? <Badge text="دارد" type="success" /> : <span className="empty-val">—</span>,
        },
        {
            name: 'تاریخ',
            id: 'createdOn',
            selector: (row) => row.createdOn,
            width: '175px',
            cell: (row) => (
                <span className="date-cell">
                    {row.createdOn
                        ? moment.utc(row.createdOn).locale('fa').local().format('YYYY/MM/DD HH:mm')
                        : '—'}
                </span>
            ),
        },
    ];

    return (
        <Page className="pmx-user-score-logs" title="تاریخچه امتیازها">
            <Card title="تاریخچه امتیازها" blur={loading}>
                {accessDenied ? (
                    <Alert
                        className="access-denied-alert"
                        message="دسترسی محدود"
                        description="شما مجوز «لیست امتیازها» را ندارید. لطفاً با مدیر سیستم تماس بگیرید."
                        type="error"
                        showIcon
                    />
                ) : (
                    <>
                        <div className="filter-row">
                            <Input
                                value={keyword}
                                className="keyword-search"
                                placeholder="جستجو با نام، نام‌خانوادگی یا شماره موبایل..."
                                name="keyword"
                                prefix={<SearchOutlined />}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                            <Button
                                text="خروجی اکسل"
                                type={buttonType.info}
                                icon={<ExportOutlined />}
                                disabled={!data?.items?.length}
                                onClick={() =>
                                    data?.items && downloadCSV('user-score-logs', flattenForExport(data.items))
                                }
                            />
                        </div>

                        <Table
                            blur={loading}
                            className="score-logs-table"
                            data={data?.items ?? []}
                            column={columns}
                            pagination={data?.pagination}
                            getdata={getAllUserScoreLogs}
                            setloader={setLoading}
                            setdata={setData}
                            filters={query}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </>
                )}
            </Card>
        </Page>
    );
};
